/**
 * useTopicResources — real-time subscription to
 *   projects/{pid}/topics/{topicId}/resources/*
 *
 * Drives `TopicResourcesSection` inside `TopicDetailPanel`. MasterPostHandler
 * also reads this collection at handler start (server-side via admin SDK)
 * so changes propagate to next master post generation.
 */

import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { Resource } from '../types';

export interface UseTopicResourcesReturn {
  resources: Resource[];
  loading: boolean;
  error: Error | null;
}

export function useTopicResources(
  projectId: string | null | undefined,
  topicId: string | null | undefined,
): UseTopicResourcesReturn {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState<boolean>(
    Boolean(projectId && topicId),
  );
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !topicId) {
      setResources([]);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = collection(
      db,
      'projects',
      projectId,
      'topics',
      topicId,
      'resources',
    );
    setLoading(true);
    const unsub = onSnapshot(
      query(ref, orderBy('createdAt', 'asc')),
      (snap) => {
        setResources(
          snap.docs.map((d) => ({ ...(d.data() as Resource), id: d.id })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, topicId]);

  return { resources, loading, error };
}
